import { useState, useEffect } from 'react';
import Brain from 'lucide-react/dist/esm/icons/brain';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SyncSettingsForm from '@/components/SyncSettingsForm';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  User,
  Mail,
  Bell,
  Shield,
  Zap,
  Database,
  Globe,
  Palette,
  Key,
  Link,
  CheckCircle2,
  AlertCircle,
  Settings2,
  Save,
  RefreshCw,
  ExternalLink,
  Plus,
  Trash2,
  Sun,
  Moon,
  Laptop,
  Sparkles,
  Waves,
  Sunset as SunsetIcon,
  Trees,
  Cpu,
  Flower2,
} from 'lucide-react';
import { demoDataService } from '@/services/demoDataService';
import { toast } from '@/hooks/use-toast';
import { blink } from '@/lib/blink';
import EmailConnectionCard from '@/components/EmailConnectionCard';
import SyncAdmin from '@/pages/SyncAdmin';
import { useTheme } from '@/contexts/ThemeContext';
import ThemePreview from '@/components/ThemePreview';
import { Separator } from '@/components/ui/separator';
import { safeJsonParse } from '@/lib/utils';
export default function Settings() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { theme: currentTheme, setPreset, setMode, updateTheme, resetToDefaults, undoChanges } = useTheme(); // Call hook at top level

  // General Settings
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [displayMode, setDisplayMode] = useState<'light' | 'dark' | 'system'>('dark');
  const [compactMode, setCompactMode] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('default');

  // AI Settings
  const [provider, setProvider] = useState<'blink' | 'openai' | 'anthropic'>('blink');
  const [model, setModel] = useState<string>('auto');
  const [responseSpeed, setResponseSpeed] = useState([3]);
  const [smartCategorization, setSmartCategorization] = useState(true);
  const [autoReply, setAutoReply] = useState(false);
  const [sentimentAnalysis, setSentimentAnalysis] = useState(true);
  const [allowAiLearning, setAllowAiLearning] = useState(true);
  const [openAIStatus, setOpenAIStatus] = useState<'unknown'|'ok'|'missing'>('unknown');
  const [anthropicStatus, setAnthropicStatus] = useState<'unknown'|'ok'|'missing'>('unknown');
  const [checkingOpenAI, setCheckingOpenAI] = useState(false);
  const [checkingAnthropic, setCheckingAnthropic] = useState(false);

  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [desktopNotifications, setDesktopNotifications] = useState(true);
  const [soundAlerts, setSoundAlerts] = useState(false);
  const [notifHighPriority, setNotifHighPriority] = useState(true);
  const [notifVipContacts, setNotifVipContacts] = useState(true);
  const [notifMeetings, setNotifMeetings] = useState(true);
  const [notifTasks, setNotifTasks] = useState(true);
  const [notifWorkflows, setNotifWorkflows] = useState(true);
  const [notifSystemUpdates, setNotifSystemUpdates] = useState(true);

  // Calendar Sync Settings
  const [autoSync, setAutoSync] = useState(true);
  const [syncInterval, setSyncInterval] = useState(15);

  // Security Settings
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [activeSessions, setActiveSessions] = useState<any[]>([]);

  // Sync display mode with current theme
  useEffect(() => {
    if (currentTheme) {
      setDisplayMode(currentTheme.mode);
    }
  }, [currentTheme]);

  // Load all settings from database
  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user && !state.isLoading) {
        setUser(state.user);
        await loadAllSettings(state.user.id);
        setLoading(false);
      }
    });
    
    return unsubscribe;
  }, []);

  const loadAllSettings = async (userId: string) => {
    try {
      // Load general settings
      const generalSettings = await blink.db.userSettings.list({
        where: { userId },
        limit: 1,
      });
      
      if (generalSettings && generalSettings.length > 0) {
        const settings = generalSettings[0];
        setDisplayName(settings.displayName || '');
        setTimezone(settings.timezone || 'UTC');
        
        // Parse theme data (could be JSON or preset name)
        const themeData = settings.theme;
        if (themeData) {
          try {
            const parsed = safeJsonParse(themeData);
            setSelectedTheme(parsed.preset || 'default');
          } catch {
            // Legacy format - just preset name
            setSelectedTheme(themeData);
          }
        }
        
        setCompactMode(Number(settings.compactMode) > 0);
      }

      // Load AI settings
      const aiSettingsData = await blink.db.aiSettings.list({
        where: { userId },
        limit: 1,
      });
      
      if (aiSettingsData && aiSettingsData.length > 0) {
        const settings = aiSettingsData[0];
        setProvider((settings.provider || 'blink') as any);
        setModel(settings.model || 'auto');
        setResponseSpeed([settings.responseSpeed || 3]);
        setSmartCategorization(Number(settings.smartCategorization) > 0);
        setAutoReply(Number(settings.autoReplySuggestions) > 0);
        setSentimentAnalysis(Number(settings.sentimentAnalysis) > 0);
        setAllowAiLearning(Number(settings.allowAiLearning) > 0);
      }

      // Load notification settings
      const notificationSettings = await blink.db.notificationSettings.list({
        where: { userId },
        limit: 1,
      });
      
      if (notificationSettings && notificationSettings.length > 0) {
        const settings = notificationSettings[0];
        setEmailNotifications(Number(settings.emailNotifications) > 0);
        setDesktopNotifications(Number(settings.desktopNotifications) > 0);
        setSoundAlerts(Number(settings.soundAlerts) > 0);
        setNotifHighPriority(Number(settings.highPriorityEmails) > 0);
        setNotifVipContacts(Number(settings.vipContacts) > 0);
        setNotifMeetings(Number(settings.meetingInvitations) > 0);
        setNotifTasks(Number(settings.taskDeadlines) > 0);
        setNotifWorkflows(Number(settings.workflowCompletions) > 0);
        setNotifSystemUpdates(Number(settings.systemUpdates) > 0);
      }

      // Load security settings
      const securitySettings = await blink.db.securitySettings.list({
        where: { userId },
        limit: 1,
      });
      
      if (securitySettings && securitySettings.length > 0) {
        const settings = securitySettings[0];
        setTwoFactorEnabled(Number(settings.twoFactorEnabled) > 0);
      }

      // Load active sessions
      const sessions = await blink.db.activeSessions.list({
        where: { userId },
        orderBy: { lastActivity: 'desc' },
      });
      
      if (sessions) {
        setActiveSessions(sessions);
      }

      // Load calendar sync preferences
      const userPrefs = await blink.db.userPreferences.list({
        where: { userId },
        limit: 1,
      });
      
      if (userPrefs && userPrefs.length > 0) {
        const prefs = userPrefs[0];
        setAutoSync(Number(prefs.autoSync) > 0);
        setSyncInterval(prefs.syncInterval || 15);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error loading settings',
        description: 'Some settings could not be loaded',
        variant: 'destructive',
      });
    }
  };

  const saveAiPrefs = () => {
    localStorage.setItem('ai_provider', provider);
    localStorage.setItem('ai_model', model);
    toast({ title: 'AI preferences saved', description: `Using ${provider === 'blink' ? 'Blink Default' : provider === 'openai' ? 'OpenAI' : 'Anthropic'} • ${model}` });
  };

  const tryOpenAIWith = async (secretName: string) => {
    try {
      const res: any = await blink.data.fetch({
        url: 'https://api.openai.com/v1/models',
        method: 'GET',
        headers: { 'Authorization': `Bearer {{${secretName}}}` }
      });
      return true; // Secret exists (regardless of external API response)
    } catch (e: any) {
      const msg = String(e?.message || e);
      if (msg.toLowerCase().includes('secret') || msg.toLowerCase().includes('not found')) return false;
      return true; // Secret substituted but external call failed => still present
    }
  };

  const checkOpenAI = async () => {
    setCheckingOpenAI(true);
    const names = ['OPENAI_API_KEY', 'openai_api_key', 'OPENAI_KEY'];
    for (const n of names) {
      const ok = await tryOpenAIWith(n);
      if (ok) { setOpenAIStatus('ok'); setCheckingOpenAI(false); return; }
    }
    setOpenAIStatus('missing');
    setCheckingOpenAI(false);
  };

  const tryAnthropicWith = async (secretName: string) => {
    try {
      const res: any = await blink.data.fetch({
        url: 'https://api.anthropic.com/v1/models',
        method: 'GET',
        headers: { 'x-api-key': `{{${secretName}}}`, 'anthropic-version': '2023-06-01' }
      });
      return true;
    } catch (e: any) {
      const msg = String(e?.message || e);
      if (msg.toLowerCase().includes('secret') || msg.toLowerCase().includes('not found')) return false;
      return true;
    }
  };

  const checkAnthropic = async () => {
    setCheckingAnthropic(true);
    const names = ['ANTHROPIC_API_KEY', 'anthropic_api_key'];
    for (const n of names) {
      const ok = await tryAnthropicWith(n);
      if (ok) { setAnthropicStatus('ok'); setCheckingAnthropic(false); return; }
    }
    setAnthropicStatus('missing');
    setCheckingAnthropic(false);
  };

  const saveSettings = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      // Save general settings
      const existingGeneral = await blink.db.userSettings.list({
        where: { userId: user.id },
        limit: 1,
      });

      if (existingGeneral && existingGeneral.length > 0) {
        await blink.db.userSettings.update(existingGeneral[0].id, {
          displayName,
          timezone,
          compactMode: compactMode ? "1" : "0",
          updatedAt: new Date().toISOString(),
        });
      } else {
        await blink.db.userSettings.create({
          id: `settings_${user.id}_${Date.now()}`,
          userId: user.id,
          displayName,
          timezone,
          compactMode: compactMode ? "1" : "0",
        });
      }
      
      // Note: Theme is saved automatically by ThemeContext

      // Save AI settings
      const existingAi = await blink.db.aiSettings.list({
        where: { userId: user.id },
        limit: 1,
      });

      if (existingAi && existingAi.length > 0) {
        await blink.db.aiSettings.update(existingAi[0].id, {
          provider,
          model,
          responseSpeed: responseSpeed[0],
          smartCategorization: smartCategorization ? "1" : "0",
          autoReplySuggestions: autoReply ? "1" : "0",
          sentimentAnalysis: sentimentAnalysis ? "1" : "0",
          allowAiLearning: allowAiLearning ? "1" : "0",
          updatedAt: new Date().toISOString(),
        });
      } else {
        await blink.db.aiSettings.create({
          id: `ai_settings_${user.id}_${Date.now()}`,
          userId: user.id,
          provider,
          model,
          responseSpeed: responseSpeed[0],
          smartCategorization: smartCategorization ? "1" : "0",
          autoReplySuggestions: autoReply ? "1" : "0",
          sentimentAnalysis: sentimentAnalysis ? "1" : "0",
          allowAiLearning: allowAiLearning ? "1" : "0",
        });
      }

      // Save notification settings
      const existingNotif = await blink.db.notificationSettings.list({
        where: { userId: user.id },
        limit: 1,
      });

      if (existingNotif && existingNotif.length > 0) {
        await blink.db.notificationSettings.update(existingNotif[0].id, {
          emailNotifications: emailNotifications ? "1" : "0",
          desktopNotifications: desktopNotifications ? "1" : "0",
          soundAlerts: soundAlerts ? "1" : "0",
          highPriorityEmails: notifHighPriority ? "1" : "0",
          vipContacts: notifVipContacts ? "1" : "0",
          meetingInvitations: notifMeetings ? "1" : "0",
          taskDeadlines: notifTasks ? "1" : "0",
          workflowCompletions: notifWorkflows ? "1" : "0",
          systemUpdates: notifSystemUpdates ? "1" : "0",
          updatedAt: new Date().toISOString(),
        });
      } else {
        await blink.db.notificationSettings.create({
          id: `notif_settings_${user.id}_${Date.now()}`,
          userId: user.id,
          emailNotifications: emailNotifications ? "1" : "0",
          desktopNotifications: desktopNotifications ? "1" : "0",
          soundAlerts: soundAlerts ? "1" : "0",
          highPriorityEmails: notifHighPriority ? "1" : "0",
          vipContacts: notifVipContacts ? "1" : "0",
          meetingInvitations: notifMeetings ? "1" : "0",
          taskDeadlines: notifTasks ? "1" : "0",
          workflowCompletions: notifWorkflows ? "1" : "0",
          systemUpdates: notifSystemUpdates ? "1" : "0",
        });
      }

      // Save calendar sync preferences
      const existingPrefs = await blink.db.userPreferences.list({
        where: { userId: user.id },
        limit: 1,
      });

      if (existingPrefs && existingPrefs.length > 0) {
        await blink.db.userPreferences.update(existingPrefs[0].id, {
          autoSync: autoSync ? "1" : "0",
          syncInterval,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await blink.db.userPreferences.create({
          id: `prefs_${user.id}_${Date.now()}`,
          userId: user.id,
          autoSync: autoSync ? "1" : "0",
          syncInterval,
        });
      }

      toast({
        title: 'Settings saved',
        description: 'Your preferences have been updated successfully',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error saving settings',
        description: 'Failed to save your preferences',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!user) return;

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all password fields',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'New password and confirmation must match',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters',
        variant: 'destructive',
      });
      return;
    }

    try {
      await blink.auth.changePassword(currentPassword, newPassword);
      
      // Update security settings
      const existingSecurity = await blink.db.securitySettings.list({
        where: { userId: user.id },
        limit: 1,
      });

      if (existingSecurity && existingSecurity.length > 0) {
        await blink.db.securitySettings.update(existingSecurity[0].id, {
          lastPasswordChange: new Date().toISOString(),
        });
      } else {
        await blink.db.securitySettings.create({
          id: `security_${user.id}_${Date.now()}`,
          userId: user.id,
          lastPasswordChange: new Date().toISOString(),
        });
      }

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      toast({
        title: 'Password updated',
        description: 'Your password has been changed successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Password change failed',
        description: error.message || 'Failed to update password',
        variant: 'destructive',
      });
    }
  };

  const handleToggle2FA = async () => {
    if (!user) return;

    // This is a placeholder - actual 2FA implementation would require more setup
    toast({
      title: 'Coming soon',
      description: 'Two-factor authentication setup will be available soon',
    });
  };

  const handleResetAiPreferences = async () => {
    if (!user) return;

    const confirmed = window.confirm('Reset all AI preferences to default?');
    if (!confirmed) return;

    try {
      setProvider('blink');
      setModel('auto');
      setResponseSpeed([3]);
      setSmartCategorization(true);
      setAutoReply(false);
      setSentimentAnalysis(true);
      setAllowAiLearning(true);

      // Update in database
      const existingAi = await blink.db.aiSettings.list({
        where: { userId: user.id },
        limit: 1,
      });

      if (existingAi && existingAi.length > 0) {
        await blink.db.aiSettings.update(existingAi[0].id, {
          provider: 'blink',
          model: 'auto',
          responseSpeed: 3,
          smartCategorization: "1",
          autoReplySuggestions: "0",
          sentimentAnalysis: "1",
          allowAiLearning: "1",
          updatedAt: new Date().toISOString(),
        });
      }

      toast({
        title: 'AI preferences reset',
        description: 'All AI settings have been restored to defaults',
      });
    } catch (error) {
      console.error('Error resetting AI preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset AI preferences',
        variant: 'destructive',
      });
    }
  };

  const handleClearDemoData = async () => {
    if (!user) return;
    
    const confirmed = window.confirm('This will permanently delete all demo data. Are you sure?');
    if (!confirmed) return;
    
    try {
      const result = await demoDataService.clearDemoData(user.id);
      if (result.success) {
        toast({
          title: 'Demo data cleared',
          description: 'All demo data has been removed from your account',
        });
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error clearing demo data:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear demo data',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account and application preferences
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving || loading} className="bg-primary hover:bg-primary/90">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="ai">AI Settings</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="sync">Sync Admin</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI User Model</CardTitle>
              <CardDescription>Your intelligent profile and behavioral analysis</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Live User Model</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = '/profile'}
                  >
                    View Full Profile
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your AI model has analyzed your communication patterns, work style, and relationships to provide intelligent automation and insights.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={user?.email || ''}
                    disabled
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Paris">Paris</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize the look and feel of your interface</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Preview with Swatches */}
              <ThemePreview 
                onThemeChange={(preset) => {
                  setSelectedTheme(preset);
                  toast({
                    title: 'Theme updated',
                    description: `Switched to ${preset} theme`,
                  });
                }}
              />

              {/* Display Mode */}
              <div className="space-y-3 pt-4 border-t border-border">
                <Label htmlFor="display-mode">Display Mode</Label>
                <Select 
                  value={displayMode} 
                  onValueChange={(v) => {
                    const newMode = v as 'light' | 'dark' | 'system';
                    setDisplayMode(newMode);
                    setMode(newMode);
                  }}
                >
                  <SelectTrigger id="display-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">
                      <div className="flex items-center gap-2">
                        <Sun className="h-4 w-4" />
                        <span>Light</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="dark">
                      <div className="flex items-center gap-2">
                        <Moon className="h-4 w-4" />
                        <span>Dark</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="system">
                      <div className="flex items-center gap-2">
                        <Laptop className="h-4 w-4" />
                        <span>System</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Choose your preferred display mode - switches between light and dark themes
                </p>
              </div>

              <Separator />

              {/* Advanced Customization */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold">Advanced Customization</h4>
                
                {/* Accent Color Override */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="accent-color">Accent Color</Label>
                    {currentTheme.accentColor && (
                      <Badge variant="secondary" className="text-xs">
                        Custom Override
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="accent-color"
                      type="color"
                      value={currentTheme.accentColor || '#F59E0B'}
                      onChange={(e) => updateTheme({ accentColor: e.target.value })}
                      className="w-20 h-10 cursor-pointer"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateTheme({ accentColor: undefined })}
                      className="flex-1"
                      disabled={!currentTheme.accentColor}
                    >
                      {currentTheme.accentColor ? 'Reset to Theme Default' : 'Using Theme Default'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {currentTheme.accentColor 
                      ? 'Custom accent color is overriding the theme default'
                      : 'Using the default accent color from the selected theme'}
                  </p>
                </div>

                {/* Border Radius Control */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="border-radius">Border Radius</Label>
                    <span className="text-sm text-muted-foreground">
                      {(currentTheme.borderRadius || 0.75).toFixed(2)}rem
                    </span>
                  </div>
                  <Slider
                    id="border-radius"
                    value={[currentTheme.borderRadius || 0.75]}
                    onValueChange={([value]) => updateTheme({ borderRadius: value })}
                    min={0}
                    max={1.5}
                    step={0.05}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Adjust the roundness of UI elements (0 = sharp, 1.5 = very round)
                  </p>
                </div>
              </div>

              <Separator />

              {/* Theme Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={undoChanges}
                  className="flex-1"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Undo Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={resetToDefaults}
                  className="flex-1"
                >
                  <Settings2 className="mr-2 h-4 w-4" />
                  Restore Defaults
                </Button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="compact">Compact Mode</Label>
                  <p className="text-sm text-muted-foreground">Show more emails in list view</p>
                </div>
                <Switch
                  id="compact"
                  checked={compactMode}
                  onCheckedChange={setCompactMode}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings */}
        <TabsContent value="ai" className="space-y-4">
          {/* AI Model Selection */}
          <Card>
            <CardHeader>
              <CardTitle>AI Model Selection</CardTitle>
              <CardDescription>Choose your preferred AI model for email intelligence</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Provider</Label>
                  <Select value={provider} onValueChange={(v) => setProvider(v as any)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blink">
                        <div className="flex items-center gap-2">
                          <span>Blink Default</span>
                          <Badge variant="secondary" className="text-xs">No keys needed</Badge>
                        </div>
                      </SelectItem>
                      <SelectItem value="openai">OpenAI (GPT-5)</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {provider === 'blink' && 'Uses Blink\'s built-in AI - no API keys required'}
                    {provider === 'openai' && 'Requires OPENAI_API_KEY in project secrets'}
                    {provider === 'anthropic' && 'Requires ANTHROPIC_API_KEY in project secrets'}
                  </p>
                </div>
                <div>
                  <Label>Model</Label>
                  {provider === 'openai' ? (
                    <Select value={model} onValueChange={setModel}>
                      <SelectTrigger>
                        <SelectValue placeholder="gpt-5" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpt-5">
                          <div className="flex items-center justify-between w-full">
                            <span>GPT-5</span>
                            <Badge variant="secondary" className="ml-2">Recommended</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="gpt-5-mini">GPT-5 Mini</SelectItem>
                        <SelectItem value="gpt-5-nano">GPT-5 Nano</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : provider === 'anthropic' ? (
                    <Select value={model} onValueChange={setModel}>
                      <SelectTrigger>
                        <SelectValue placeholder="claude-4.5" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="claude-4.5">
                          <div className="flex items-center justify-between w-full">
                            <span>Claude 4.5</span>
                            <Badge variant="secondary" className="ml-2">Recommended</Badge>
                          </div>
                        </SelectItem>
                        <SelectItem value="claude-3.7-sonnet">Claude 3.7 Sonnet</SelectItem>
                        <SelectItem value="claude-3.7-haiku">Claude 3.7 Haiku</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="auto" disabled />
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Select the AI model for email analysis and chat
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveAiPrefs} className="ml-auto bg-primary hover:bg-primary/90">
                  <Save className="mr-2 h-4 w-4" /> Save AI Model Preferences
                </Button>
              </div>
              
              {/* Key verification section - only show when provider needs keys */}
              {(provider === 'openai' || provider === 'anthropic') && (
                <div className="mt-6 p-4 border rounded-lg bg-muted/20">
                  <div className="flex items-start gap-3 mb-4">
                    <Key className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <h4 className="font-medium text-sm">API Key Required</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        To use {provider === 'openai' ? 'OpenAI' : 'Anthropic'} models, add your API key as a project secret.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {provider === 'openai' && (
                      <div className="p-3 border rounded-lg flex items-center justify-between bg-background">
                        <div>
                          <p className="text-sm font-medium">OpenAI Key Status</p>
                          <p className="text-xs text-muted-foreground">Project secret: OPENAI_API_KEY</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={openAIStatus==='ok' ? 'secondary' : openAIStatus==='missing' ? 'destructive' : 'outline'} className="text-xs">
                            {openAIStatus==='ok' ? '✓ Detected' : openAIStatus==='missing' ? '✗ Missing' : '? Unknown'}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={checkOpenAI} disabled={checkingOpenAI}>
                            {checkingOpenAI ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Verify'}
                          </Button>
                        </div>
                      </div>
                    )}
                    {provider === 'anthropic' && (
                      <div className="p-3 border rounded-lg flex items-center justify-between bg-background">
                        <div>
                          <p className="text-sm font-medium">Anthropic Key Status</p>
                          <p className="text-xs text-muted-foreground">Project secret: ANTHROPIC_API_KEY</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={anthropicStatus==='ok' ? 'secondary' : anthropicStatus==='missing' ? 'destructive' : 'outline'} className="text-xs">
                            {anthropicStatus==='ok' ? '✓ Detected' : anthropicStatus==='missing' ? '✗ Missing' : '? Unknown'}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={checkAnthropic} disabled={checkingAnthropic}>
                            {checkingAnthropic ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Verify'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <Alert className="mt-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Keys are stored securely in project secrets and never exposed to the browser. Add them via your project settings.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Behavior Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>AI Behavior</CardTitle>
              <CardDescription>Customize how AI processes your emails</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Response Speed</Label>
                  <span className="text-sm text-muted-foreground">{responseSpeed[0]}s</span>
                </div>
                <Slider
                  value={responseSpeed}
                  onValueChange={setResponseSpeed}
                  min={1}
                  max={10}
                  step={1}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Faster responses may be less detailed
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="smart-cat">Smart Categorization</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically categorize incoming emails
                    </p>
                  </div>
                  <Switch
                    id="smart-cat"
                    checked={smartCategorization}
                    onCheckedChange={setSmartCategorization}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-reply">Auto-Reply Suggestions</Label>
                    <p className="text-sm text-muted-foreground">
                      Generate reply suggestions automatically
                    </p>
                  </div>
                  <Switch
                    id="auto-reply"
                    checked={autoReply}
                    onCheckedChange={setAutoReply}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="sentiment">Sentiment Analysis</Label>
                    <p className="text-sm text-muted-foreground">
                      Analyze email tone and sentiment
                    </p>
                  </div>
                  <Switch
                    id="sentiment"
                    checked={sentimentAnalysis}
                    onCheckedChange={setSentimentAnalysis}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>AI Training Data</CardTitle>
              <CardDescription>Manage your AI training preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow AI Learning</Label>
                  <p className="text-sm text-muted-foreground">
                    Help improve AI by learning from your usage patterns
                  </p>
                </div>
                <Switch
                  checked={allowAiLearning}
                  onCheckedChange={setAllowAiLearning}
                />
              </div>
              <Button variant="outline" className="w-full" onClick={handleResetAiPreferences}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset AI Preferences
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Calendar Auto-Sync</CardTitle>
              <CardDescription>Automatically sync your calendar in the background</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-sync">Enable Auto-Sync</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically sync calendar events every {syncInterval} minutes
                  </p>
                </div>
                <Switch
                  id="auto-sync"
                  checked={autoSync}
                  onCheckedChange={setAutoSync}
                />
              </div>

              {autoSync && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="sync-interval">Sync Interval</Label>
                    <span className="text-sm text-muted-foreground">{syncInterval} minutes</span>
                  </div>
                  <Slider
                    id="sync-interval"
                    value={[syncInterval]}
                    onValueChange={([value]) => setSyncInterval(value)}
                    min={5}
                    max={60}
                    step={5}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    How often to check for new calendar events (5-60 minutes)
                  </p>
                </div>
              )}

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  When auto-sync is enabled, calendar events are automatically fetched in the background. The UI updates automatically without needing manual refresh.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="email-notif">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive important updates via email
                  </p>
                </div>
                <Switch
                  id="email-notif"
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="desktop-notif">Desktop Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Show notifications on your desktop
                  </p>
                </div>
                <Switch
                  id="desktop-notif"
                  checked={desktopNotifications}
                  onCheckedChange={setDesktopNotifications}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sound">Sound Alerts</Label>
                  <p className="text-sm text-muted-foreground">Play sound for new emails</p>
                </div>
                <Switch
                  id="sound"
                  checked={soundAlerts}
                  onCheckedChange={setSoundAlerts}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notification Types</CardTitle>
              <CardDescription>Select which events trigger notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-normal">High priority emails</Label>
                <Switch
                  checked={notifHighPriority}
                  onCheckedChange={setNotifHighPriority}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="font-normal">Emails from VIP contacts</Label>
                <Switch
                  checked={notifVipContacts}
                  onCheckedChange={setNotifVipContacts}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="font-normal">Meeting invitations</Label>
                <Switch
                  checked={notifMeetings}
                  onCheckedChange={setNotifMeetings}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="font-normal">Task deadlines</Label>
                <Switch
                  checked={notifTasks}
                  onCheckedChange={setNotifTasks}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="font-normal">Workflow completions</Label>
                <Switch
                  checked={notifWorkflows}
                  onCheckedChange={setNotifWorkflows}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="font-normal">System updates</Label>
                <Switch
                  checked={notifSystemUpdates}
                  onCheckedChange={setNotifSystemUpdates}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="space-y-4">
          <EmailConnectionCard onConnectionChange={() => loadAllSettings(user?.id)} />

          <Card>
            <CardHeader>
              <CardTitle>Third-Party Integrations</CardTitle>
              <CardDescription>Connect with your favorite tools</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: 'Slack', status: 'not-connected' },
                  { name: 'Microsoft Teams', status: 'not-connected' },
                  { name: 'Google Calendar', status: 'not-connected' },
                  { name: 'Trello', status: 'not-connected' },
                  { name: 'Notion', status: 'not-connected' },
                  { name: 'Zapier', status: 'not-connected' },
                  { name: 'Outlook Email', status: 'not-connected' },
                ].map((integration) => (
                  <div
                    key={integration.name}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <span className="text-sm font-medium">{integration.name}</span>
                    {integration.status === 'connected' ? (
                      <Badge variant="outline" className="text-xs">
                        <CheckCircle2 className="mr-1 h-3 w-3 text-green-500" />
                        Connected
                      </Badge>
                    ) : (
                      <Button variant="outline" size="sm">
                        Connect
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Admin */}
        <TabsContent value="sync" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sync Retry Configuration</CardTitle>
              <CardDescription>
                Configure retry behavior for email and calendar sync operations (saved to database)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <SyncSettingsForm userId={user?.id} />
            </CardContent>
          </Card>

          <SyncAdmin />
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Password & Authentication</CardTitle>
              <CardDescription>Manage your security settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={handlePasswordChange}>Update Password</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Two-Factor Authentication</CardTitle>
              <CardDescription>Add an extra layer of security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Status</p>
                  <p className="text-sm text-muted-foreground">
                    Two-factor authentication is currently {twoFactorEnabled ? 'enabled' : 'disabled'}
                  </p>
                </div>
                <Button variant="outline" onClick={handleToggle2FA}>
                  <Shield className="mr-2 h-4 w-4" />
                  {twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Active Sessions</CardTitle>
              <CardDescription>Manage your active login sessions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activeSessions.length > 0 ? (
                  activeSessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div>
                        <p className="font-medium">
                          {Number(session.isCurrent) > 0 ? 'Current Session' : 'Session'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {session.deviceInfo || 'Unknown device'} • {session.location || 'Unknown location'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Last active: {new Date(session.lastActivity).toLocaleString()}
                        </p>
                      </div>
                      <Badge variant="outline">
                        {Number(session.isCurrent) > 0 ? 'Current' : 'Active'}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">Current Session</p>
                      <p className="text-sm text-muted-foreground">
                        This device • Active now
                      </p>
                    </div>
                    <Badge variant="outline">Active</Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}